import type { BusinessService } from "../../types";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";
import { createEntityId } from "./internal/entityId";
import { serviceMapService } from "./serviceMapService";
import { computeAnchor, findFreePosition } from "../../utils/graph";

const API_PATH = "/v1/business-service";

const ensureNodeExists = (
  systemMap: Awaited<ReturnType<typeof serviceMapService.get>>,
  id: string,
  action: string,
): number => {
  const nodeIdx = systemMap.nodes.findIndex((n) => n.id === id);
  if (nodeIdx === -1) {
    throw new Error(`businessServiceService.${action}: Node ${id} doesn't exists`);
  }
  return nodeIdx;
};

export const businessServiceService = {
  create: async (
    serviceMapId: string,
    system: Omit<BusinessService, "status">,
    dependencies: string[],
    dependents: string[],
  ): Promise<BusinessService & { id: string; serviceMapId: string }> => {
    const createdService: BusinessService = {
      ...system,
      id: createEntityId(),
      status: "healthy",
    };

    const systemMap = await serviceMapService.get(serviceMapId);
    const existingNode = systemMap.nodes.find((node) => node.id === createdService.id);
    if (existingNode) {
      throw new Error(`Node ${createdService.label} already exists`);
    }

    const anchorIds = Array.from(new Set([...dependencies, ...dependents]));
    const anchor = computeAnchor(systemMap.nodes as any, anchorIds);
    const position = findFreePosition(systemMap.nodes as any, anchor);

    systemMap.nodes.push({
      id: createdService.id,
      type: "default",
      position,
      data: createdService,
    });

    for (const dep of dependencies) {
      const edgeId = `${dep}#${createdService.id}`;
      const edgeExists = systemMap.edges.some((e) => e.id === edgeId);
      if (!edgeExists) {
        systemMap.edges.push({
          id: edgeId,
          source: dep,
          target: createdService.id,
          type: "default",
        });
      }
    }

    for (const dep of dependents) {
      const edgeId = `${createdService.id}#${dep}`;
      const edgeExists = systemMap.edges.some((e) => e.id === edgeId);
      if (!edgeExists) {
        systemMap.edges.push({
          id: edgeId,
          source: createdService.id,
          target: dep,
          type: "default",
        });
      }
    }

    await serviceMapService.patch(serviceMapId, systemMap);

    return {
      ...createdService,
      serviceMapId,
    };
  },

  getServicesByAlertId: async (alertId: string) => {
    return sigv4ApiClient.list<BusinessService & { id: string; serviceMapId: string }>(
      API_PATH,
      { alertId },
    );
  },

  delete: async (serviceMapId: string, systemId: string): Promise<void> => {
    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(systemId)}`, {
      serviceMapId,
    });
  },

  removeDependencies: async (
    serviceMapId: string,
    id: string,
    dependencies: string[],
  ) => {
    if (dependencies.length === 0) {
      return serviceMapId;
    }
    const systemMap = await serviceMapService.get(serviceMapId);
    ensureNodeExists(systemMap, id, "removeDependencies");

    for (const dep of dependencies) {
      systemMap.edges = systemMap.edges.filter((e) => e.id !== `${dep}#${id}`);
    }

    await serviceMapService.patch(serviceMapId, systemMap);
    return serviceMapId;
  },

  removeDependents: async (
    serviceMapId: string,
    id: string,
    dependents: string[],
  ) => {
    if (dependents.length === 0) {
      return serviceMapId;
    }
    const systemMap = await serviceMapService.get(serviceMapId);
    ensureNodeExists(systemMap, id, "removeDependents");

    for (const dep of dependents) {
      systemMap.edges = systemMap.edges.filter((e) => e.id !== `${id}#${dep}`);
    }

    await serviceMapService.patch(serviceMapId, systemMap);
    return serviceMapId;
  },

  addDependencies: async (
    serviceMapId: string,
    id: string,
    dependencies: string[],
  ) => {
    if (dependencies.length === 0) {
      return serviceMapId;
    }
    const systemMap = await serviceMapService.get(serviceMapId);
    ensureNodeExists(systemMap, id, "addDependencies");

    for (const dep of dependencies) {
      const edgeId = `${dep}#${id}`;
      const edgeExists = systemMap.edges.some((e) => e.id === edgeId);
      if (!edgeExists) {
        systemMap.edges.push({
          id: edgeId,
          source: dep,
          target: id,
          type: "default",
        });
      }
    }

    await serviceMapService.patch(serviceMapId, systemMap);
    return serviceMapId;
  },

  addDependents: async (
    serviceMapId: string,
    id: string,
    dependents: string[],
  ) => {
    if (dependents.length === 0) {
      return serviceMapId;
    }
    const systemMap = await serviceMapService.get(serviceMapId);
    ensureNodeExists(systemMap, id, "addDependents");

    for (const dep of dependents) {
      const edgeId = `${id}#${dep}`;
      const edgeExists = systemMap.edges.some((e) => e.id === edgeId);
      if (!edgeExists) {
        systemMap.edges.push({
          id: edgeId,
          source: id,
          target: dep,
          type: "default",
        });
      }
    }

    await serviceMapService.patch(serviceMapId, systemMap);
    return serviceMapId;
  },

  patch: async (
    serviceMapId: string,
    id: string,
    patch: Partial<BusinessService>,
  ): Promise<string> => {
    const systemMap = await serviceMapService.get(serviceMapId);
    const nodeIdx = ensureNodeExists(systemMap, id, "patch");

    systemMap.nodes[nodeIdx].data = {
      ...systemMap.nodes[nodeIdx].data,
      ...patch,
    };

    await serviceMapService.patch(serviceMapId, systemMap);
    return serviceMapId;
  },
};
